import React, { useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, KeyRound, Loader2, Phone, UserRound } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { api } from '../lib/api';
import GoogleSignInButton from './GoogleSignInButton';
import { clearUserAuth } from '../lib/auth';

export default function OtpAuthCard({
  mode = 'login',
  title = 'Continue with phone',
  subtitle = 'No password needed. We will send you a 6 digit OTP.',
}) {
  const nav = useNavigate();
  const [step, setStep] = useState('phone');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [authComplete, setAuthComplete] = useState(false);
  const requestRef = useRef(false);
  const verifyRef = useRef(false);
  const cleanOtpValue = otp.replace(/\D/g, '').slice(0, 6);
  const clearExistingSession = async () => {
    clearUserAuth();
    try {
      await api.post('/auth/logout');
    } catch {
      // Best effort: local auth is already cleared, and OTP can continue.
    }
  };

  const requestOtp = async (e) => {
    e.preventDefault();
    if (loading || requestRef.current) return;
    requestRef.current = true;
    setLoading(true);
    try {
      await clearExistingSession();
      const { data } = await api.post('/auth/otp/request', { phone, name, mode });
      setDevOtp(data.dev_otp || '');
      setOtp('');
      setAuthComplete(false);
      setStep('otp');
      toast.success(data.dev_otp ? 'OTP ready below' : 'OTP sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send OTP');
    } finally {
      requestRef.current = false;
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    const cleanOtp = otp.replace(/\D/g, '').slice(0, 6);
    if (loading || authComplete || verifyRef.current || cleanOtp.length !== 6) return;
    verifyRef.current = true;
    setLoading(true);
    try {
      const { data } = await api.post('/auth/otp/verify', { phone, otp: cleanOtp, name, mode });
      setAuthComplete(true);
      localStorage.setItem('hb_token', data.token);
      if (data.refresh_token) localStorage.setItem('hb_refresh_token', data.refresh_token);
      localStorage.setItem('hb_user', JSON.stringify(data.user));
      localStorage.removeItem('hb_doctor');
      toast.success(`Welcome, ${data.user.name}`);
      try {
        await window.OneSignal?.login?.(data.user.phone || data.user.id);
      } catch {
        // Notification registration should not block a successful login.
      }
      nav('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid OTP');
    } finally {
      verifyRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16 fade-up">
      <Card className="p-8 rounded-3xl border border-[#E8E1D5] bg-white hb-shadow-lg">
        <div className="text-center">
          <div className="h-14 w-14 rounded-2xl bg-[#1E3F33] grid place-items-center mx-auto">
            <Phone className="h-7 w-7 text-white" />
          </div>
          <h1 className="font-display text-3xl text-[#1A1A1A] mt-4">{title}</h1>
          <p className="text-[#4A4A4A] text-sm mt-2">{subtitle}</p>
        </div>

        {/* Portal Switcher Toggle */}
        <div className="flex rounded-full bg-[#F4F1EA] p-1 mt-6 border border-[#E8E1D5]">
          <button
            type="button"
            className="flex-1 py-2 text-xs font-semibold rounded-full transition-all bg-[#1E3F33] text-white shadow-sm"
          >
            Patient Access
          </button>
          <button
            type="button"
            onClick={() => nav(mode === 'register' ? '/doctor/register' : '/doctor/login')}
            className="flex-1 py-2 text-xs font-semibold rounded-full text-[#7A7A7A] hover:text-[#1E3F33] transition-all"
          >
            Doctor Access
          </button>
        </div>

        <div className="mt-8">
          <GoogleSignInButton mode={mode} text={mode === 'register' ? 'signup_with' : 'signin_with'} />
        </div>

        <div className="my-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-wide text-[#9A9A9A]">
          <div className="h-px flex-1 bg-[#E8E1D5]" />
          <span>or use phone</span>
          <div className="h-px flex-1 bg-[#E8E1D5]" />
        </div>

        {step === 'phone' ? (
          <form onSubmit={requestOtp} className="space-y-4">
            {mode === 'register' ? (
              <div>
                <Label className="font-semibold text-[#1E3F33]">Full name</Label>
                <div className="relative mt-1.5">
                  <UserRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required className="h-12 pl-10 rounded-xl border-[#E2E8F0]" />
                </div>
              </div>
            ) : null}
            <div>
              <Label className="font-semibold text-[#1E3F33]">Phone number</Label>
              <div className="relative mt-1.5">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
                <Input value={phone} onChange={(e) => { setPhone(e.target.value); setAuthComplete(false); }} placeholder="+919876543210" required className="h-12 pl-10 rounded-xl border-[#E2E8F0]" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Send OTP <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </form>
        ) : (
          <form onSubmit={verifyOtp} className="mt-8 space-y-4">
            <div>
              <Label className="font-semibold text-[#1E3F33]">Enter OTP</Label>
              <div className="relative mt-1.5">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
                <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="6 digit code" required className="h-12 pl-10 rounded-xl border-[#E2E8F0] tracking-[0.35em]" />
              </div>
              {devOtp ? (
                <div className="mt-3 rounded-xl border border-[#E8E1D5] bg-[#F4F1EA] px-4 py-3 text-sm text-[#1A1A1A]">
                  Test OTP: <span className="font-bold tracking-[0.25em] text-[#1E3F33]">{devOtp}</span>
                </div>
              ) : (
                <p className="text-xs text-[#7A7A7A] mt-2">Check your SMS or WhatsApp for the OTP.</p>
              )}
            </div>
            <Button type="submit" disabled={loading || authComplete || cleanOtpValue.length !== 6} className="w-full h-12 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {authComplete ? 'Continuing...' : 'Verify and continue'}
            </Button>
            <button type="button" onClick={() => { setStep('phone'); setOtp(''); setAuthComplete(false); }} className="w-full text-sm font-semibold text-[#1E3F33]">
              Change phone number
            </button>
          </form>
        )}

        <div className="mt-5 text-center text-sm text-[#7A7A7A]">
          {mode === 'login' ? (
            <>
              No account? <Link to="/register" className="text-[#1E3F33] font-semibold">Sign up</Link>
            </>
          ) : (
            <>
              Already have an account? <Link to="/login" className="text-[#1E3F33] font-semibold">Login</Link>
            </>
          )}
        </div>
        <div className="mt-2 text-center text-sm text-[#7A7A7A]">
          Doctor? <Link to="/doctor/login" className="text-[#1E3F33] font-semibold">Use doctor portal</Link>
        </div>
      </Card>
    </div>
  );
}
